
import React from 'react';

interface ProcessStep {
  number: number;
  title: string;
  description: string;
}

interface ProcessTimelineProps {
  steps: ProcessStep[];
}

const ProcessTimeline: React.FC<ProcessTimelineProps> = ({ steps }) => {
  return (
    <div className="relative max-w-4xl mx-auto">
      {/* Subtle Background Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-secondary-brand/5 via-transparent to-secondary-brand/5 blur-3xl"></div>
      
      <div className="relative space-y-6 md:space-y-8">
        {steps.map((step, index) => (
          <div 
            key={step.number} 
            className="group relative flex items-start gap-4 md:gap-8 fade-in-up"
            style={{ animationDelay: `${index * 0.15}s` }}
          >
            {/* Professional Connecting Line */}
            {index < steps.length - 1 && (
              <div className="absolute left-6 md:left-8 top-16 md:top-20 w-0.5 h-16 md:h-20 bg-gradient-to-b from-secondary-brand via-secondary-brand/40 to-transparent opacity-40 group-hover:opacity-70 transition-opacity duration-300"></div>
            )}
            
            {/* Professional Step Number */}
            <div className="relative flex-shrink-0">
              {/* Elegant Outer Glow */}
              <div className="absolute inset-0 bg-gradient-secondary rounded-full blur-md opacity-20 group-hover:opacity-40 transition-opacity duration-300 scale-110"></div>
              
              {/* Number Container */}
              <div className="relative w-12 h-12 md:w-16 md:h-16 bg-gradient-secondary rounded-full flex items-center justify-center shadow-elegant group-hover:scale-110 transition-all duration-300 border-4 border-white/20">
                <span className="text-white font-bold text-lg md:text-xl">
                  {step.number}
                </span>
                
                {/* Subtle Inner Highlight */}
                <div className="absolute inset-2 bg-white/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
            
            {/* Professional Content Card */}
            <div className="flex-1 min-h-[100px] md:min-h-[120px] group-hover:transform group-hover:translate-x-1 transition-transform duration-300">
              <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl md:rounded-2xl p-4 md:p-6 h-full group-hover:bg-white/15 group-hover:border-white/30 group-hover:shadow-soft transition-all duration-300">
                {/* Content Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10 space-y-2 md:space-y-3">
                  <h3 className="text-lg md:text-xl font-bold text-white group-hover:text-secondary-brand transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-300 leading-relaxed group-hover:text-white transition-colors duration-300">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProcessTimeline;
